import { Tag, type IconName, type TagVariant } from '@dev-spendesk/grapes';
import { type ReactNode } from 'react';

import { useTranslation } from 'src/core/common/hooks/useTranslation';
import type { I18nKey } from 'src/core/common/hooks/useTranslation';
import type { InconsistencyMatchingResult } from 'src/core/modules/verification-of-payee/models';

import './InconsistencyModalItem.css';

type Props = {
  itemSummary: ReactNode;
  matchingResult: InconsistencyMatchingResult;
};

type TagProps = {
  variant: TagVariant;
  iconName: IconName;
  label: I18nKey;
};

const useTagProps = (matchingResult: InconsistencyMatchingResult): TagProps => {
  switch (matchingResult) {
    case 'close_match':
      return {
        variant: 'warning',
        iconName: 'triangle-warning',
        label: 'inconsistencyModal.payment.partialMatch',
      };
    case 'no_match':
      return {
        variant: 'alert',
        iconName: 'hexagone-cross',
        label: 'inconsistencyModal.payment.noMatch',
      };
    case 'impossible_match':
      return {
        variant: 'alert',
        iconName: 'hexagone-cross',
        label: 'inconsistencyModal.payment.impossibleMatch',
      };
    default:
      throw new Error(`Invalid matching result: ${matchingResult}`);
  }
};

export const InconsistencyModalItem = ({
  itemSummary,
  matchingResult,
}: Props) => {
  const { t } = useTranslation('global');

  const { variant, iconName, label } = useTagProps(matchingResult);

  return (
    <div className="inconsistency-modal-content-line bg-background-secondary-default border-border-default border">
      {itemSummary}
      <Tag variant={variant} iconName={iconName}>
        {t(label)}
      </Tag>
    </div>
  );
};
